// MaxDriver storefront behaviour for the md2-* sections (redesign v3).
// Everything is delegated from the document, so sections only add data-*
// attributes. Without JS every control still works: product forms post to
// /cart/add natively and package forms post to /cart/add before checkout.

import { CartLinesUpdateEvent } from '@shopify/events';

const routes = () => window.Theme?.routes ?? { cart_add_url: '/cart/add.js', cart_url: '/cart', cart_update_url: '/cart/update' };

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

/** Section ids of every cart-items-component, so the drawer morphs in place. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

/**
 * Adds lines and announces them with the standard cart event: the Horizon
 * cart drawer auto-opens on `add`, and the icon bubble updates.
 * @param {{ id: number|string, quantity: number }[]} items
 * @param {EventTarget} dispatcher
 */
export function addLines(items, dispatcher = document) {
  const deferred = CartLinesUpdateEvent.createPromise();
  dispatcher.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  return fetch(routes().cart_add_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      items: items.map((item) => ({
        id: Number(item.id),
        quantity: item.quantity,
        ...(item.properties ? { properties: item.properties } : {}),
      })),
      sections: cartSectionIds(),
    }),
  })
    .then((response) => response.json())
    .then(async (response) => {
      const didError = Boolean(response.status);
      const ajaxCart = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, sections: response.sections, source: 'md2', didError },
      });
      if (didError) throw new Error(response.description || response.message || 'add failed');
      return ajaxCart;
    })
    .catch((error) => {
      deferred.reject(error);
      throw error;
    });
}

/** Flashes a temporary label on a button, then restores it. */
function flash(button, label, ms = 1800) {
  const original = button.dataset.md2Label ?? button.innerHTML;
  button.dataset.md2Label = original;
  button.innerHTML = label;
  window.setTimeout(() => {
    button.innerHTML = original;
  }, ms);
}

/* Quick add: <button data-md2-add="variantId" data-md2-added="Adăugat"> */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-md2-add]') : null;
  if (!button) return;
  event.preventDefault();
  if (button.classList.contains('is-loading')) return;
  button.classList.add('is-loading');
  addLines([{ id: button.getAttribute('data-md2-add'), quantity: 1 }], button)
    .then(() => flash(button, button.getAttribute('data-md2-added') || '✓'))
    .catch(() => flash(button, button.getAttribute('data-md2-error') || '!'))
    .finally(() => button.classList.remove('is-loading'));
});

/* Product form: <form data-md2-product-form> with name=id and name=quantity */
document.addEventListener('submit', (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.matches('[data-md2-product-form]')) return;
  event.preventDefault();
  const data = new FormData(form);
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-md2-error]');
  if (error) error.hidden = true;
  button?.classList.add('is-loading');
  addLines([{ id: String(data.get('id')), quantity: Number(data.get('quantity') || 1) }], form)
    .catch((err) => {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    })
    .finally(() => button?.classList.remove('is-loading'));
});

/*
 * Package checkout: <form data-md2-checkout> whose checked radio carries
 * data-updates='{"variantId": qty, ...}'. /cart/update.js sets absolute
 * quantities, so a returning visitor never ends up with two cameras, other
 * lines (accessories added on purpose) stay, and a stale add-on is removed.
 */
document.addEventListener('submit', (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.matches('[data-md2-checkout]')) return;
  const checked = form.querySelector('input[type="radio"]:checked');
  const raw = checked?.getAttribute('data-updates');
  if (!raw) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-md2-error]');
  if (error) error.hidden = true;
  button?.classList.add('is-loading');
  fetch(`${routes().cart_update_url}.js`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ updates: JSON.parse(raw) }),
  })
    .then((response) => {
      if (!response.ok) throw new Error('update');
      window.location.href = '/checkout';
    })
    .catch(() => {
      button?.classList.remove('is-loading');
      if (error) error.hidden = false;
    });
});

/* Package radios: mirror the selected price into every [data-md2-pkg-price] */
document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-md2-pkg]')) return;
  const form = input.form;
  if (!form) return;
  form.querySelectorAll('[data-md2-pkg-price]').forEach((el) => {
    el.textContent = input.getAttribute('data-price') || '';
  });
  document.querySelectorAll('[data-md2-sticky-price]').forEach((el) => {
    el.textContent = input.getAttribute('data-price') || '';
  });
});

/*
 * Scroll position across both scrollers: Horizon scrolls div.page-wrapper on
 * desktop (≥990px) and the window on mobile.
 */
export function onScroll(callback) {
  const wrapper = document.querySelector('.page-wrapper');
  const run = () => callback(Math.max(window.scrollY, wrapper?.scrollTop ?? 0));
  window.addEventListener('scroll', run, { passive: true });
  wrapper?.addEventListener('scroll', run, { passive: true });
  run();
}

/* Smooth in-page anchors that respect both scrollers and the sticky header */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href^="#md2-"]') : null;
  if (!link) return;
  const target = document.getElementById(link.getAttribute('href').slice(1));
  if (!target) return;
  event.preventDefault();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
});

/*
 * Buy box: <form data-md2-buy> with name=id (the product) and an optional
 * add-on checkbox [data-md2-addon] whose value is the add-on variant id.
 * "cart" adds the lines (the drawer opens); "checkout" makes the cart hold
 * exactly one product + the chosen add-on, then goes to checkout. Lines that
 * are missing are added through /cart/add (so add-to-cart events fire); lines
 * already there are set to the wanted quantity, never doubled.
 */
/** Line item properties for the main product (e.g. the free gift note) */
function mainProps(form) {
  /** @type {Record<string, string>} */
  const props = {};
  form.querySelectorAll('[data-md2-prop]').forEach((input) => {
    const name = input.getAttribute('name') || '';
    props[name.slice('properties['.length, -1)] = /** @type {HTMLInputElement} */ (input).value;
  });
  return Object.keys(props).length ? props : undefined;
}

async function buyNow(form) {
  const data = new FormData(form);
  const main = String(data.get('id'));
  const props = mainProps(form);
  const want = { [main]: 1 };
  form.querySelectorAll('[data-md2-addon]').forEach((input) => {
    want[/** @type {HTMLInputElement} */ (input).value] = /** @type {HTMLInputElement} */ (input).checked ? 1 : 0;
  });
  form.querySelectorAll('[data-md2-extra]').forEach((input) => {
    want[/** @type {HTMLInputElement} */ (input).value] = 1;
  });
  const cart = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const have = {};
  cart.items.forEach((item) => {
    have[item.variant_id] = (have[item.variant_id] || 0) + item.quantity;
  });
  const toAdd = [];
  const updates = {};
  Object.entries(want).forEach(([id, qty]) => {
    if (!have[id] && qty > 0) toAdd.push({ id, quantity: qty, ...(id === main && props ? { properties: props } : {}) });
    else if (have[id] && have[id] !== qty) updates[id] = qty;
  });
  if (Object.keys(updates).length) {
    const res = await fetch(`${routes().cart_update_url}.js`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error('update');
  }
  if (toAdd.length) await addLines(toAdd, form);
  window.location.href = '/checkout';
}

document.addEventListener('submit', (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.matches('[data-md2-buy]')) return;
  event.preventDefault();
  const submitter = /** @type {SubmitEvent} */ (event).submitter;
  const action = submitter?.getAttribute('data-md2-action') || 'cart';
  const error = form.querySelector('[data-md2-error]');
  if (error) error.hidden = true;
  submitter?.classList.add('is-loading');
  const data = new FormData(form);
  const addons = /** @type {HTMLInputElement[]} */ ([...form.querySelectorAll('[data-md2-addon]')]);
  const run =
    action === 'checkout'
      ? buyNow(form)
      : addLines(
          [
            { id: String(data.get('id')), quantity: 1, properties: mainProps(form) },
            ...addons.filter((input) => input.checked).map((input) => ({ id: input.value, quantity: 1 })),
            ...[...form.querySelectorAll('[data-md2-extra]')].map((input) => ({
              id: /** @type {HTMLInputElement} */ (input).value,
              quantity: 1,
            })),
          ],
          form
        );
  run
    .catch(() => {
      if (error) error.hidden = false;
    })
    .finally(() => {
      if (action !== 'checkout') submitter?.classList.remove('is-loading');
    });
});

/*
 * Add-on toggles: the form carries the total for every on/off combination
 * (data-t<mask> formatted, data-c<mask> in cents, bit n = add-on n). Mirror the
 * total into [data-md2-total] and swap the free/paid shipping tag.
 */
document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-md2-addon]')) return;
  const form = input.form;
  if (!form) return;
  let mask = 0;
  form.querySelectorAll('[data-md2-addon]').forEach((el) => {
    if (/** @type {HTMLInputElement} */ (el).checked) mask |= 1 << Number(el.getAttribute('data-md2-addon') || 0);
  });
  form.classList.toggle('has-addon', mask > 0);
  const total = form.getAttribute(`data-t${mask}`) || '';
  document.querySelectorAll('[data-md2-total]').forEach((el) => {
    el.textContent = total;
  });
  const free = Number(form.getAttribute('data-free')) || 0;
  const isFree = free > 0 && Number(form.getAttribute(`data-c${mask}`)) >= free;
  form.closest('[data-md2-pdp]')?.querySelectorAll('[data-md2-ship]').forEach((el) => {
    /** @type {HTMLElement} */ (el).hidden = (el.getAttribute('data-md2-ship') === 'free') !== isFree;
  });
});

/* Proxy buttons (sticky bar): <button data-md2-proxy="#id"> clicks the target */
document.addEventListener('click', (event) => {
  const proxy = event.target instanceof Element ? event.target.closest('[data-md2-proxy]') : null;
  if (!proxy) return;
  const target = document.querySelector(proxy.getAttribute('data-md2-proxy'));
  if (!(target instanceof HTMLElement)) return;
  event.preventDefault();
  target.click();
});

/*
 * Campaign window: [data-md2-bf] shows only between data-start and data-end
 * (epoch seconds), [data-md2-bf-off] is its inverse (the regular content).
 * The server renders the right state; this keeps cached pages honest.
 */
function bfTick() {
  const now = Date.now() / 1000;
  document.querySelectorAll('[data-md2-bf], [data-md2-bf-off]').forEach((el) => {
    const start = Number(el.getAttribute('data-start'));
    const end = Number(el.getAttribute('data-end'));
    if (!start || !end) return;
    const live = now >= start && now <= end;
    el.hidden = el.hasAttribute('data-md2-bf') ? !live : live;
  });
  document.querySelectorAll('[data-md2-cd]').forEach((el) => {
    const end = Number(el.getAttribute('data-md2-cd'));
    let left = Math.max(0, Math.floor(end - now));
    const parts = { d: Math.floor(left / 86400), h: Math.floor((left % 86400) / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
    Object.entries(parts).forEach(([key, value]) => {
      const slot = el.querySelector(`[data-${key}]`);
      if (slot) slot.textContent = String(value).padStart(2, '0');
    });
  });
}
if (document.querySelector('[data-md2-bf], [data-md2-bf-off], [data-md2-cd]')) {
  bfTick();
  window.setInterval(bfTick, 1000);
}

/* Signup links ("/#md2-email") stay on the page when it carries its own form */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href$="#md2-email"]') : null;
  const form = link ? document.getElementById('md2-email') : null;
  if (!form) return;
  event.preventDefault();
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  form.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  history.replaceState(null, '', '#md2-email');
});

/* Cookie preferences: reopen Shopify's consent banner, else go to the policy */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-md2-cookie-prefs]') : null;
  if (!button) return;
  event.preventDefault();
  const banner = /** @type {any} */ (window).privacyBanner;
  if (banner?.showPreferences) {
    banner.showPreferences();
    return;
  }
  const fallback = button.getAttribute('data-fallback');
  if (fallback) window.location.href = fallback;
});

/* Tabs: [data-md2-tabs] with [role=tab][aria-controls] and matching panels */
document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-md2-tabs] [role="tab"]') : null;
  if (!tab) return;
  const list = tab.closest('[data-md2-tabs]');
  list?.querySelectorAll('[role="tab"]').forEach((other) => {
    const on = other === tab;
    other.setAttribute('aria-selected', on ? 'true' : 'false');
    const panel = document.getElementById(other.getAttribute('aria-controls') || '');
    if (panel) panel.hidden = !on;
    if (!on) panel?.querySelectorAll('video').forEach((v) => v.pause());
  });
});

/*
 * YouTube on demand: <button data-md2-yt="VIDEO_ID"> becomes a
 * youtube-nocookie iframe only when clicked, so no YouTube cookies or
 * requests happen before the visitor asks for the video.
 */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-md2-yt]') : null;
  if (!button) return;
  event.preventDefault();
  const id = button.getAttribute('data-md2-yt');
  const frame = document.createElement('iframe');
  frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id || '')}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  frame.title = button.getAttribute('aria-label') || 'YouTube';
  frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  frame.allowFullscreen = true;
  frame.className = 'md2-yt__frame';
  button.replaceWith(frame);
});

/*
 * Looping media videos (.md2-mf video, md-story video): load and play only while
 * on screen, pause when scrolled away. With reduced motion they stay paused,
 * controls on. Videos with sound (.md2-mf--sound) are left to the visitor's tap.
 */
const lazyVideos = document.querySelectorAll('.md2-mf:not(.md2-mf--sound) video, video.md-story__visual');
if (lazyVideos.length) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) {
    lazyVideos.forEach((video) => {
      video.controls = true;
      video.preload = 'metadata';
    });
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            if (video.preload === 'none') video.preload = 'auto';
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    lazyVideos.forEach((video) => io.observe(video));
  }
}

window.MD2 = { addLines, onScroll };
